import QuantumParticleAnalyzer from './QuantumParticleAnalyzer';
import CosmicPhenomenaAnalyzer from './CosmicPhenomenaAnalyzer';

export class QuantumDataAnalyzer {
  constructor() {
    this.quantumReadings = new Map();
    this.anomalyThresholds = {
      coherence: 0.85,      // Increased from 0.7
      entanglement: 0.9,    // Increased from 0.8
      frequency: 1e10,      // Increased from 1e8
      phase: Math.PI / 1.5  // Decreased from PI/2 (making stable phase range smaller)
    };
    this.particleAnalyzer = new QuantumParticleAnalyzer();
    this.cosmicAnalyzer = new CosmicPhenomenaAnalyzer();
  }

  analyzeQuantumSignature(metrics) {
    const {
      frequency,
      quantum_coherence,
      entanglement_coefficient,
      quantum_phase,
      particle_data,
      cosmic_data,
      realityLayer
    } = metrics;

    // Use layer seed as a quantum decoder key
    const layerSeed = parseInt(realityLayer, 10);
    if (isNaN(layerSeed)) {
      throw new Error('Invalid reality layer seed for quantum decoding');
    }

    // Apply quantum decoding using layer seed
    const decodedMetrics = this.decodeQuantumSignal({
      frequency,
      quantum_coherence,
      entanglement_coefficient,
      quantum_phase
    }, layerSeed);

    // Process decoded quantum metrics
    const standardAnalysis = {
      coherenceAnalysis: this.analyzeCohesion(decodedMetrics.quantum_coherence),
      frequencySpectrum: this.analyzeFrequency(decodedMetrics.frequency),
      entanglementMetrics: this.analyzeEntanglement(decodedMetrics.entanglement_coefficient),
      phaseCharacteristics: this.analyzePhase(decodedMetrics.quantum_phase)
    };

    // Process particle data with decoded metrics
    const particleProperties = this.decodeParticleData(particle_data, layerSeed);

    // Process cosmic data with decoded metrics
    const cosmicPhenomena = this.decodeCosmicData(cosmic_data, layerSeed);

    // Combine all analyses with decoded data
    return {
      ...standardAnalysis,
      particleProperties,
      cosmicPhenomena,
      anomalyDetection: this.detectAnomalies({
        ...decodedMetrics,
        particleProperties,
        cosmicPhenomena
      }),
      decodedSignature: {
        layerSeed,
        signalStrength: this.calculateSignalStrength(decodedMetrics),
        quantumStability: this.calculateStability(decodedMetrics),
        resonancePattern: this.calculateResonance(decodedMetrics, layerSeed)
      },
      // Ensure we always have valid metrics even if empty
      metrics: {
        coherence: decodedMetrics.quantum_coherence || 0,
        entanglement: decodedMetrics.entanglement_coefficient || 0,
        phase: decodedMetrics.quantum_phase || 0,
        frequency: decodedMetrics.frequency || 0
      }
    };
  }

  analyzeCohesion(coherence) {
    const validCoherence = typeof coherence === 'number' ? coherence : 0;
    return {
      stabilityIndex: validCoherence * 100,
      decoherenceRate: Math.exp(-validCoherence * 2),
      quantumFidelity: Math.pow(validCoherence, 2) * 100,
      coherenceClass: validCoherence > this.anomalyThresholds.coherence ? 'High' : 'Low'
    };
  }

  analyzeFrequency(freq) {
    // Ensure we have a valid frequency value
    const frequency = typeof freq === 'number' ? freq : 0;
    
    // Calculate frequency metrics only if we have a non-zero frequency
    if (frequency > 0) {
      const logFreq = Math.log10(frequency);
      return {
        bandwidthHz: frequency,
        spectralDensity: frequency / 1e6,
        frequencyBand: this.classifyFrequencyBand(frequency),
        harmonicComponents: this.detectHarmonics(frequency)
      };
    }
    
    // Return default values if frequency is invalid
    return {
      bandwidthHz: 0,
      spectralDensity: 0,
      frequencyBand: 'Low-Band',
      harmonicComponents: []
    };
  }

  analyzeEntanglement(entanglement) {
    const validEntanglement = typeof entanglement === 'number' ? entanglement : 0;
    return {
      correlationStrength: validEntanglement * 100,
      entanglementDegree: Math.ceil(validEntanglement * 5),
      couplingEfficiency: Math.pow(validEntanglement, 2) * 100,
      entanglementType: validEntanglement > this.anomalyThresholds.entanglement ? 'Strong' : 'Weak'
    };
  }

  analyzePhase(phase) {
    const validPhase = typeof phase === 'number' ? phase : 0;
    return {
      phaseAngle: validPhase * 180 / Math.PI,
      coherenceLength: Math.cos(validPhase) * 100,
      phaseStability: Math.abs(Math.sin(validPhase)) * 100,
      phaseState: validPhase < this.anomalyThresholds.phase ? 'Stable' : 'Unstable'
    };
  }

  detectAnomalies(metrics) {
    const anomalies = [];
    const {
      frequency,
      quantum_coherence,
      entanglement_coefficient,
      quantum_phase,
      particleProperties,
      cosmicPhenomena
    } = metrics;

    // Standard quantum anomalies
    if (quantum_coherence > this.anomalyThresholds.coherence) {
      anomalies.push({
        type: 'High Coherence',
        value: quantum_coherence.toFixed(3),
        significance: 'Critical'
      });
    }

    if (frequency > this.anomalyThresholds.frequency) {
      anomalies.push({
        type: 'Frequency Spike',
        value: frequency.toExponential(2),
        significance: 'Warning'
      });
    }

    if (entanglement_coefficient > this.anomalyThresholds.entanglement) {
      anomalies.push({
        type: 'Strong Entanglement',
        value: entanglement_coefficient.toFixed(3),
        significance: 'Alert'
      });
    }

    if (quantum_phase > this.anomalyThresholds.phase) {
      anomalies.push({
        type: 'Phase Instability',
        value: (quantum_phase * 180 / Math.PI).toFixed(2) + '°',
        significance: 'Caution'
      });
    }

    // Particle anomalies
    if (particleProperties?.tunneling?.probability > 0.1) {
      anomalies.push({
        type: 'High Tunneling',
        value: particleProperties.tunneling.probability,
        significance: 'Warning'
      });
    }

    if (particleProperties?.decayProperties?.halfLife < 1e-12) {
      anomalies.push({
        type: 'Rapid Decay',
        value: particleProperties.decayProperties.halfLife + ' s',
        significance: 'Alert'
      });
    }

    // Cosmic phenomena anomalies
    if (cosmicPhenomena?.gravitationalWaves?.detected) {
      anomalies.push({
        type: 'Gravitational Wave',
        value: cosmicPhenomena.gravitationalWaves.strain,
        significance: 'Critical'
      });
    }

    if (cosmicPhenomena?.astronomicalEvents?.events?.length > 1) {
      anomalies.push({
        type: 'Multiple Cosmic Events',
        value: cosmicPhenomena.astronomicalEvents.events.length + ' events',
        significance: 'Warning'
      });
    }

    if (cosmicPhenomena?.pulsarAnalysis?.detected) {
      anomalies.push({
        type: 'Pulsar Signal',
        value: cosmicPhenomena.pulsarAnalysis.period,
        significance: 'Alert'
      });
    }

    return {
      detected: anomalies.length > 0,
      count: anomalies.length,
      anomalies: anomalies
    };
  }

  classifyFrequencyBand(frequency) {
    if (frequency < 1e6) return 'Low-Band';
    if (frequency < 1e9) return 'Mid-Band';
    return 'High-Band';
  }

  detectHarmonics(frequency) {
    const harmonics = [];
    for (let i = 2; i <= 4; i++) {
      harmonics.push({
        order: i,
        frequency: (frequency * i).toExponential(2) + ' Hz'
      });
    }
    return harmonics;
  }

  decodeQuantumSignal(metrics, layerSeed) {
    // Use layer seed to generate a unique quantum interference pattern
    const seedPhase = (layerSeed * Math.PI) / 180;
    const decodingMatrix = this.generateDecodingMatrix(seedPhase);

    return {
      frequency: metrics.frequency * decodingMatrix.frequencyMod,
      quantum_coherence: metrics.quantum_coherence * decodingMatrix.coherenceMod,
      entanglement_coefficient: metrics.entanglement_coefficient * decodingMatrix.entanglementMod,
      quantum_phase: (metrics.quantum_phase + decodingMatrix.phaseShift) % (2 * Math.PI)
    };
  }

  generateDecodingMatrix(seedPhase) {
    return {
      frequencyMod: 1 + Math.sin(seedPhase) * 0.5,
      coherenceMod: 1 + Math.cos(seedPhase) * 0.3,
      entanglementMod: 1 + Math.sin(2 * seedPhase) * 0.4,
      phaseShift: seedPhase * Math.PI
    };
  }

  decodeParticleData(particleData, layerSeed) {
    if (!particleData) return null;

    const decodingFactor = Math.abs(Math.sin(layerSeed)) + 0.5;
    const { particleType, spinState, decayProperties, tunneling } = particleData;

    return {
      particleType: {
        ...particleType,
        energy: (parseFloat(particleType.energy) * decodingFactor).toExponential(2),
        confidence: Math.min(100, parseFloat(particleType.confidence) * decodingFactor).toFixed(1)
      },
      spinState: {
        ...spinState,
        probability: Math.min(1, parseFloat(spinState.probability) * decodingFactor).toFixed(3)
      },
      decayProperties: {
        ...decayProperties,
        halfLife: (parseFloat(decayProperties.halfLife) / decodingFactor).toExponential(2)
      },
      tunneling: {
        ...tunneling,
        probability: Math.min(1, parseFloat(tunneling.probability) * decodingFactor).toExponential(3)
      }
    };
  }

  decodeCosmicData(cosmicData, layerSeed) {
    if (!cosmicData) return null;

    const decodingFactor = 1 + (layerSeed % 10) / 10;
    const { pulsarAnalysis, gravitationalWaves, backgroundRadiation, astronomicalEvents } = cosmicData;

    return {
      pulsarAnalysis: {
        ...pulsarAnalysis,
        period: (parseFloat(pulsarAnalysis.period) * decodingFactor).toExponential(2),
        signalStrength: Math.min(1, parseFloat(pulsarAnalysis.signalStrength) * decodingFactor).toFixed(3)
      },
      gravitationalWaves: {
        ...gravitationalWaves,
        strain: (parseFloat(gravitationalWaves.strain) * decodingFactor).toExponential(2)
      },
      backgroundRadiation: {
        ...backgroundRadiation,
        temperature: (parseFloat(backgroundRadiation.temperature) * decodingFactor).toFixed(6)
      },
      astronomicalEvents: {
        ...astronomicalEvents,
        events: astronomicalEvents.events.map(event => ({
          ...event,
          intensity: Math.min(1, event.intensity * decodingFactor)
        }))
      }
    };
  }

  calculateSignalStrength(decodedMetrics) {
    const { quantum_coherence, entanglement_coefficient } = decodedMetrics;
    return Math.min(100, (quantum_coherence + entanglement_coefficient) * 50).toFixed(1);
  }

  calculateStability(decodedMetrics) {
    const { quantum_phase, quantum_coherence } = decodedMetrics;
    return Math.min(100, (1 - Math.abs(Math.sin(quantum_phase))) * quantum_coherence * 100).toFixed(1);
  }

  calculateResonance(decodedMetrics, layerSeed) {
    const { frequency } = decodedMetrics;
    const resonanceBase = Math.sin(frequency * layerSeed * 1e-10);
    return {
      amplitude: Math.abs(resonanceBase).toFixed(3),
      phase: ((resonanceBase + 1) * Math.PI).toFixed(3),
      frequency: (frequency * (1 + Math.abs(resonanceBase))).toExponential(2)
    };
  }

  getActiveReadings() {
    return Array.from(this.quantumReadings.values())
      .filter(reading => reading.active)
      .map(reading => ({
        id: reading.id,
        timestamp: reading.timestamp,
        metrics: reading.metrics
      }));
  }
}

export default QuantumDataAnalyzer;